import { TierSchema } from '@developerz/domain';
import { t } from '../i18n';
import { decimal, utcMonthDay } from './time';

// The platform radar's trend math (#6715): pure, so every rule the cards and the
// 30-day chart obey is a unit test, not a render.
//
// THE SERIES. `GET /v1/admin/overview` `trends[metric]` is the stored history, one
// `{ day, value }` per UTC day the snapshot cron (or the backfill) wrote, day
// ascending, a missing day ABSENT. The chart's last point is not from it: it is the
// LIVE number the same body carries (`accounts.total`, a `by_tier` count …), so the
// radar never shows a number that disagrees with itself. A stored point for today
// is dropped in favour of the live one.
//
// THE COMPARISON is against the point EXACTLY `WEEK` days back. A tier card has no
// such point until its first snapshot is a week old (decision of record: never
// back-attribute), and it says so — `{ kind: 'none' }` — rather than comparing
// against whatever older point happens to exist under a "vs 7 days ago" label.

/** The comparison window, days. */
export const WEEK = 7;
/** The chart's range, days. */
export const RANGE = 30;
/** The x-axis ticks, in days ago. */
export const TICKS = [30, 21, 14, 7, 0] as const;

const DAY_MS = 86_400_000;

export type Tone = 'good' | 'bad' | 'flat';
/** One chart point; `ago` is whole days before today (0 = today). */
export type SeriesPoint = { readonly day: string; readonly ago: number; readonly value: number };
export type Comparison =
  | { readonly kind: 'none' }
  | { readonly kind: 'delta'; readonly prev: number; readonly delta: number; readonly tone: Tone };

/** Today's UTC day key, the same `'YYYY-MM-DD'` the series carries. */
export const utcToday = (now: number): string => new Date(now).toISOString().slice(0, 10);

/** `day` moved by `by` whole days (negative = earlier). */
export const shiftDay = (day: string, by: number): string =>
  new Date(Date.parse(day) + by * DAY_MS).toISOString().slice(0, 10);

/** "Today" for day 0, else "Oct 14". */
export const dayLabel = (day: string, today: string): string =>
  day === today ? t('admin.trend_today') : utcMonthDay(day);

/** The window's stored points (today excluded), then today's live value. */
export function chartSeries(
  points: readonly { readonly day: string; readonly value: number }[],
  live: number,
  today: string,
): readonly SeriesPoint[] {
  const base = Date.parse(today);
  const past = points
    .map((p) => ({ ...p, ago: Math.round((base - Date.parse(p.day)) / DAY_MS) }))
    .filter((p) => p.ago > 0 && p.ago <= RANGE);
  return [...past, { day: today, ago: 0, value: live }];
}

/** Up is good, down is bad, none is flat — mirrored for an inverse metric (Waitlisted). */
export function tone(delta: number, inverse: boolean): Tone {
  if (delta === 0) return 'flat';
  return delta > 0 !== inverse ? 'good' : 'bad';
}

/** Today's value against the point exactly `ago` days back, if one exists. */
export function compareTo(
  series: readonly SeriesPoint[],
  ago: number,
  inverse: boolean,
): Comparison {
  const then = series.find((p) => p.ago === ago);
  const now = series[series.length - 1];
  if (then === undefined || now === undefined || then === now) return { kind: 'none' };
  const delta = now.value - then.value;
  return { kind: 'delta', prev: then.value, delta, tone: tone(delta, inverse) };
}

/** The direction glyph a delta leads with ('' for none); decorative, the sign carries it. */
export const arrow = (delta: number): string => (delta > 0 ? '▲' : delta < 0 ? '▼' : '');

/** "+3", "−3" (a real minus), "±0". */
export function signed(delta: number): string {
  if (delta === 0) return '±0';
  return `${delta > 0 ? '+' : '−'}${decimal(Math.abs(delta))}`;
}

/** The delta as a share of the old value: "+22%" (whole from 10%), "−4.2%", "0%", or "new" from zero. */
export function percent(delta: number, prev: number): string {
  if (delta === 0) return '0%';
  if (prev === 0) return t('admin.trend_new');
  const p = Math.abs((delta / prev) * 100);
  return `${delta > 0 ? '+' : '−'}${decimal(p >= 10 ? Math.round(p) : p)}%`;
}

/** SVG user space the chart draws in; overlays are placed in percent of it. */
export const VIEW_W = 1000;
export const VIEW_H = 160;

export type ChartGeometry = {
  /** Gridline / y-label values: max, mid (when the range allows one), min. */
  readonly grid: readonly number[];
  /** A value's y in `VIEW_H` units. */
  readonly y: (value: number) => number;
  /** The line: steps (a stock), or straight segments (a flow such as Runs last 24h). */
  readonly line: string;
  /** The line closed down to the baseline, for the tinted fill. */
  readonly area: string;
};

/** A day-ago's x in `VIEW_W` units: 30 days ago is the left edge, today the right. */
export const xOf = (ago: number): number => ((RANGE - ago) / RANGE) * VIEW_W;

export function chartGeometry(series: readonly SeriesPoint[], flow: boolean): ChartGeometry {
  const values = series.map((p) => p.value);
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  // A flat series still needs a band to draw in; never below zero for a count.
  if (hi === lo) {
    lo = Math.max(0, lo - 1);
    hi += 1;
  }
  const y = (v: number): number => VIEW_H - 12 - ((v - lo) / (hi - lo)) * (VIEW_H - 24);
  const grid = hi - lo >= 2 ? [hi, Math.round((hi + lo) / 2), lo] : [hi, lo];
  const line = series
    .map((p, i) => {
      const x = xOf(p.ago).toFixed(1);
      const py = y(p.value).toFixed(1);
      if (i === 0) return `M${x} ${py}`;
      return flow ? `L${x} ${py}` : `H${x} V${py}`;
    })
    .join(' ');
  const first = xOf(series[0]?.ago ?? 0).toFixed(1);
  return { grid, y, line, area: `${line} L${VIEW_W} ${VIEW_H} L${first} ${VIEW_H} Z` };
}

/** A `VIEW_*` coordinate as a CSS percentage of the plot ("42.50%"). */
export const pctOf = (v: number, of: number): string => `${((v / of) * 100).toFixed(2)}%`;

/**
 * The crosshair's readout as one sentence, for the chart's live region: the day, its
 * value, the change from the previous stored day (absent on the first point) and
 * the change from that day to today.
 */
export function readout(series: readonly SeriesPoint[], index: number, today: string): string {
  const point = series[index];
  const now = series[series.length - 1];
  if (point === undefined || now === undefined) return '';
  const prev = series[index - 1];
  const base = {
    date: dayLabel(point.day, today),
    value: decimal(point.value),
    to_today: signed(now.value - point.value),
  };
  return prev === undefined
    ? t('admin.trend_readout_first', base)
    : t('admin.trend_readout', {
        ...base,
        delta: signed(point.value - prev.value),
        prev: dayLabel(prev.day, today),
      });
}

const TIER_PREFIX = 'accounts.tier.';
const KNOWN_TIERS: readonly string[] = TierSchema.options;

/**
 * The Accounts band's tier cards: every tier with a live account (`by_tier`) OR any
 * stored history (`trends['accounts.tier.<tier>']`), so a tier that fell to zero
 * still shows its fall (the snapshot writes its 0 rows) instead of vanishing. Known
 * tiers first in the domain's order, then any other in the order met.
 */
export function trendTiers(
  byTier: Readonly<Record<string, number>>,
  trends: Readonly<Record<string, readonly unknown[]>>,
): readonly { readonly tier: string; readonly live: number }[] {
  const met = new Set([
    ...Object.keys(byTier),
    ...Object.keys(trends)
      .filter((k) => k.startsWith(TIER_PREFIX))
      .map((k) => k.slice(TIER_PREFIX.length)),
  ]);
  const ordered = [
    ...KNOWN_TIERS.filter((tier) => met.has(tier)),
    ...[...met].filter((tier) => !KNOWN_TIERS.includes(tier)),
  ];
  return ordered
    .map((tier) => ({ tier, live: byTier[tier] ?? 0 }))
    .filter((r) => r.live > 0 || (trends[`${TIER_PREFIX}${r.tier}`]?.length ?? 0) > 0);
}

/** The series index nearest a day-ago (the hover crosshair snaps to a stored day). */
export function nearestIndex(series: readonly SeriesPoint[], ago: number): number {
  let best = 0;
  series.forEach((p, i) => {
    if (Math.abs(p.ago - ago) < Math.abs((series[best]?.ago ?? 0) - ago)) best = i;
  });
  return best;
}
